import config from '../config';
import selectedEnvironment from '../env';

const API_URL = `${config[selectedEnvironment].API_URL}/ExpenseAdjustments`;

/** Fired on window after adjustments were transferred to the matching report; detail: { caseId, year }. */
export const EXPENSE_ADJUSTMENTS_TRANSFERRED_EVENT = 'expenseAdjustments:transferred';

/** The Tax Authority's vehicle use value calculator (חישוב שווי שימוש ברכב). */
export const USE_VALUE_CALCULATOR_URL = 'https://secapp.taxes.gov.il/mm_usecar10/UseCarScreen.aspx';

/** Matches the server's ExpenseAdjustmentCategory. */
export const ADJUSTMENT_CATEGORY = {
    VEHICLE: 0,
    MOBILE_PHONE: 1,
    REFRESHMENTS: 2,
    WORK_CLOTHING: 3,
    OTHER: 4,
};

/** Matches the server's AssetType. */
export const ASSET_TYPE = {
    GENERAL: 0,
    VEHICLE: 1,
};

/** Matches the server's VehicleUsageKind. */
export const VEHICLE_USAGE = {
    PRIVATE: 0,
    COMMERCIAL: 1,
};

/** Matches the server's VehicleCostKind. */
export const VEHICLE_COST_KIND = {
    VARIABLE: 0,
    FIXED: 1,
};

/** Matches the server's VehicleCostSplit: how a shared expense is divided between the vehicles. */
export const VEHICLE_COST_SPLIT = {
    EQUAL: 0,
    PERCENTAGES: 1,
};

/** Matches the server's VehicleAdjustmentType, with the recognized share of the expenses. */
export const VEHICLE_ADJUSTMENT_TYPES = [
    { value: 0, labelKey: 'VehicleAdjustmentRegular', percentage: 45 },
    { value: 1, labelKey: 'VehicleAdjustmentTaxi', percentage: 90 },
    { value: 2, labelKey: 'VehicleAdjustmentPatrol', percentage: 80 },
    { value: 3, labelKey: 'VehicleAdjustmentDrivingLearning', percentage: 77.5 },
    { value: 4, labelKey: 'VehicleAdjustmentDrivingInstruction', percentage: 68 },
    { value: 5, labelKey: 'VehicleAdjustmentAttached', percentage: 100 },
    { value: 6, labelKey: 'VehicleAdjustmentMotorcycle', percentage: 25 },
];

const handleResponse = async (response) => {
    if (!response.ok) {
        let message = '';
        try {
            const body = await response.json();
            message = body?.message || '';
        } catch {
            message = '';
        }
        const error = new Error(message || `HTTP ${response.status}`);
        error.key = message;
        throw error;
    }
    return response.json();
};

const post = async (path, body, token) =>
    handleResponse(await fetch(`${API_URL}/${path}`, {
        method: 'POST',
        headers: {
            Authorization: 'Bearer ' + token,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
    }));

const getOverview = ({ caseId, year, isFromProfitAndLossDetailed = null }, token) =>
    post('GetOverview', { caseId, year, isFromProfitAndLossDetailed }, token);

const saveSettings = ({ caseId, settings }, token) => post('SaveSettings', { caseId, settings }, token);

/**
 * The car expenses screen: `vehicles` [{ assetId, variableCosts, fixedCosts, filterByDocumentDate }] and the shared
 * `pools` [{ id, name, costKind, sourceType, codes, filterByDocumentDate, split, shares: [{ assetId, percentage }] }].
 * Returns the year's overview.
 */
const saveVehicleExpenses = ({ caseId, year, isFromProfitAndLossDetailed = null, vehicles, pools }, token) =>
    post('SaveVehicleExpenses', { caseId, year, isFromProfitAndLossDetailed, vehicles, pools }, token);

const saveVehicleYear = (input, isFromProfitAndLossDetailed, token) => {
    const query = isFromProfitAndLossDetailed === null || isFromProfitAndLossDetailed === undefined
        ? ''
        : `?isFromProfitAndLossDetailed=${isFromProfitAndLossDetailed}`;
    return post(`SaveVehicleYear${query}`, input, token);
};

const transfer = ({ caseId, year, isFromProfitAndLossDetailed = null, keys = [] }, token) =>
    post('Transfer', { caseId, year, isFromProfitAndLossDetailed, keys }, token);

const toIntOrNull = (value) => {
    const number = Number(value);
    return value === '' || value === null || value === undefined || !Number.isFinite(number) || number === 0
        ? null
        : Math.trunc(number);
};

const getVehicleUseValue = ({ licenseNumber, manufacturerCode, modelCode, modelYear }, token) =>
    post('VehicleUseValue', {
        licenseNumber: licenseNumber ? String(licenseNumber).trim() : null,
        manufacturerCode: toIntOrNull(manufacturerCode),
        modelCode: toIntOrNull(modelCode),
        modelYear: toIntOrNull(modelYear),
    }, token);

/** Same rounding as the server's ExpenseAdjustmentCalculator.MonthlyUseValue (2.48% of the list price). */
export const monthlyUseValueFromPrice = (listPrice) => {
    const price = Number(listPrice);
    if (!Number.isFinite(price) || price <= 0) return null;
    return Math.round((price * 0.0248) / 10) * 10;
};

/** Price groups of vehicles registered before 2010 (קבוצת מחיר); null = by the list price. */
export const USE_VALUE_GROUPS = [1, 2, 3, 4, 5, 6, 7];

const catalogCache = new Map();

/** The price list barely changes, so each list is loaded once per page load; failures are not cached. */
const cachedPost = (cacheKey, path, body, token) => {
    if (!catalogCache.has(cacheKey)) {
        const request = post(path, body, token).catch((error) => {
            catalogCache.delete(cacheKey);
            throw error;
        });
        catalogCache.set(cacheKey, request);
    }
    return catalogCache.get(cacheKey);
};

const getVehicleManufacturers = (token) => cachedPost('manufacturers', 'VehicleManufacturers', {}, token);

const getVehicleModelYears = (manufacturerCode, token) =>
    cachedPost(`years:${manufacturerCode}`, 'VehicleModelYears', { manufacturerCode }, token);

const getVehicleModels = (manufacturerCode, modelYear, token) =>
    cachedPost(`models:${manufacturerCode}:${modelYear}`, 'VehicleModels', { manufacturerCode, modelYear }, token);

/** The use value in a tax year: by price group, or the list price linked from the registration year and capped. */
const getVehicleUseValueForYear = ({ taxYear, listPrice, registrationYear, useValueGroup }, token) => {
    const body = {
        taxYear,
        listPrice: Number(listPrice) > 0 ? Number(listPrice) : null,
        registrationYear: toIntOrNull(registrationYear),
        useValueGroup: toIntOrNull(useValueGroup),
    };
    return cachedPost(`useValue:${JSON.stringify(body)}`, 'VehicleUseValueForYear', body, token);
};

const ExpenseAdjustmentsService = {
    getOverview,
    saveSettings,
    saveVehicleExpenses,
    saveVehicleYear,
    transfer,
    getVehicleUseValue,
    getVehicleUseValueForYear,
    getVehicleManufacturers,
    getVehicleModelYears,
    getVehicleModels,
};

export default ExpenseAdjustmentsService;
